/**
 * AI Command Center — Milkath's AI Co-Pilot workspace.
 *
 * Consumes /api/ai/brief, /api/ai/ask, /api/ai/timeline, /api/reports.
 * NEVER computes business metrics — everything is grounded in the BI Engine
 * through the AI service.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Send, TrendingUp, AlertTriangle, Target, ListChecks,
  MessageSquare, Clock, Upload, RefreshCw, Info,
} from "lucide-react";
import { api } from "../lib/api";
import { formatDate } from "../lib/format";

const priorityDot = (p) =>
  p === "high" ? "bg-red-500" : p === "medium" ? "bg-amber-500" : "bg-zinc-500";

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section className="space-y-3" data-testid={testid}>
      <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-2">
        <Icon className="h-3.5 w-3.5" /> {title}
      </h2>
      {children}
    </section>
  );
}

function AskBox({ onAnswer }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    if (!q.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/ask", { question: q.trim() });
      setAnswer(data);
      onAnswer?.(data);
    } catch (err) {
      toast.error("Couldn't reach the AI service. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const suggestions = [
    "Why did revenue change this month?",
    "Which products need my attention?",
    "How can I improve my Health Score?",
    "Which marketplace is growing fastest?",
  ];

  return (
    <div className="rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-900/60 to-zinc-950 p-5 sm:p-6" data-testid="ai-ask-box">
      <form onSubmit={submit} className="flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2.5 focus-within:border-zinc-600 transition-colors">
          <Sparkles className="h-4 w-4 text-orange-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ask your AI Co-Pilot anything about your business…"
            className="flex-1 bg-transparent outline-none text-sm text-zinc-100 placeholder-zinc-500"
            data-testid="ai-ask-input"
          />
        </div>
        <button
          type="submit"
          disabled={!q.trim() || busy}
          data-testid="ai-ask-submit"
          className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-40 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors"
        >
          {busy ? "Thinking…" : "Ask"}
          <Send className="h-3.5 w-3.5" />
        </button>
      </form>
      <div className="mt-3 flex flex-wrap gap-2">
        {suggestions.map((s) => (
          <button
            key={s}
            onClick={() => setQ(s)}
            data-testid={`ai-suggest-${s.slice(0, 8).replace(/\s/g, "-")}`}
            className="text-xs px-2.5 py-1 rounded-md bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 border border-zinc-800 transition-colors"
          >
            {s}
          </button>
        ))}
      </div>

      {answer && (
        <div className="mt-5 pt-5 border-t border-zinc-800/70" data-testid="ai-ask-answer">
          <p className="text-sm text-zinc-100 leading-relaxed whitespace-pre-wrap">{answer.answer}</p>
          <div className="mt-3 flex items-center gap-3 flex-wrap text-[10px] uppercase tracking-[0.16em] text-zinc-500">
            <span>Confidence · {Math.round((answer.confidence || 0) * 100)}%</span>
            <span>· {answer.time_period}</span>
            {answer.sources?.length > 0 && (
              <span data-testid="ai-ask-sources">Sources · {answer.sources.slice(0, 5).join(", ")}</span>
            )}
          </div>
          {answer.recommended_actions?.length > 0 && (
            <ul className="mt-3 space-y-1">
              {answer.recommended_actions.map((a, i) => (
                <li key={i} className="text-xs text-zinc-300 flex items-start gap-2">
                  <Target className="h-3 w-3 text-orange-400 mt-0.5" /> {a}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function BriefSummary({ brief }) {
  if (!brief) return null;
  const s = brief.sections;
  return (
    <section className="rounded-3xl border border-zinc-800 bg-gradient-to-br from-zinc-900/70 to-zinc-950 p-6 sm:p-8" data-testid="ai-brief">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500/80">Today's Business Brief · {formatDate(brief.date)}</p>
      <h1 className="mt-1 text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }} data-testid="ai-brief-greeting">
        {s.greeting}
      </h1>
      <ul className="mt-5 space-y-2" data-testid="ai-brief-summary">
        {(s.summary || []).map((line, i) => (
          <li key={i} className="text-sm text-zinc-200 leading-relaxed flex items-start gap-2">
            <span className="mt-2 h-1 w-1 rounded-full bg-orange-400 shrink-0" />
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CardList({ items, testid, empty, renderItem }) {
  if (!items?.length) return <p className="text-sm text-zinc-500" data-testid={`${testid}-empty`}>{empty}</p>;
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid={testid}>
      {items.map((item, i) => renderItem(item, i))}
    </div>
  );
}

export default function AICommandCenter() {
  const [brief, setBrief] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [b, t, s] = await Promise.all([
        api.get("/ai/brief"),
        api.get("/ai/timeline?limit=15"),
        api.get("/reports/suggested"),
      ]);
      setBrief(b.data); setTimeline(t.data); setSuggestion(s.data.suggestion);
    } catch (e) {
      toast.error("Couldn't load your AI brief.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading && !brief) {
    return <div className="text-sm text-zinc-500" data-testid="ai-loading">Preparing your business brief…</div>;
  }
  const s = brief?.sections || {};

  return (
    <div className="max-w-7xl mx-auto space-y-10" data-testid="ai-command-center">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500/80 mb-2">AI Co-Pilot</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>
            Your business analyst
          </h1>
          <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
            Every insight is grounded in Milkath's Business Intelligence Engine. Never hallucinated.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="ai-refresh"
          className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-zinc-200 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 transition-colors"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* Brief */}
      <BriefSummary brief={brief} />

      {/* Ask */}
      <AskBox />

      {/* Priorities */}
      <Section icon={Target} title="Today's priorities" testid="ai-priorities-section">
        <CardList
          items={s.priorities} testid="ai-priorities" empty="No priorities today."
          renderItem={(p, i) => (
            <div key={i} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid={`ai-priority-${i}`}>
              <div className="flex items-center gap-2">
                <span className={`h-1.5 w-1.5 rounded-full ${priorityDot(p.priority)}`} />
                <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-400 font-bold">{p.priority}</span>
              </div>
              <p className="mt-1 text-sm text-zinc-100 font-medium">{p.title}</p>
              <p className="mt-1 text-xs text-zinc-400 leading-relaxed">{p.reason}</p>
              {p.impact && <p className="mt-2 text-xs text-zinc-300"><span className="text-zinc-500">Impact:</span> {p.impact}</p>}
            </div>
          )}
        />
      </Section>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Risks */}
        <Section icon={AlertTriangle} title="Business risks" testid="ai-risks-section">
          {(s.risks || []).length === 0 ? (
            <p className="text-sm text-zinc-500">No risks detected.</p>
          ) : (
            <div className="space-y-3" data-testid="ai-risks">
              {(s.risks || []).map((r, i) => (
                <div key={i} className="rounded-xl border border-red-500/30 bg-red-500/5 p-4" data-testid={`ai-risk-${i}`}>
                  <p className="text-sm text-zinc-100 font-medium">{r.title}</p>
                  <p className="mt-1 text-xs text-zinc-400 leading-relaxed">{r.description}</p>
                  {r.confidence != null && <p className="mt-2 text-[10px] uppercase tracking-[0.16em] text-zinc-500">Confidence · {Math.round(r.confidence * 100)}%</p>}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Growth opportunities */}
        <Section icon={TrendingUp} title="Growth opportunities" testid="ai-growth-section">
          {(s.opportunities || []).length === 0 ? (
            <p className="text-sm text-zinc-500">No opportunities right now.</p>
          ) : (
            <div className="space-y-3" data-testid="ai-growth">
              {(s.opportunities || []).map((o, i) => (
                <div key={i} className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid={`ai-growth-${i}`}>
                  <p className="text-sm text-zinc-100 font-medium">{o.title}</p>
                  <p className="mt-1 text-xs text-zinc-400 leading-relaxed">{o.description}</p>
                  {o.estimated_impact && <p className="mt-2 text-xs text-zinc-300"><span className="text-zinc-500">Estimated impact:</span> {o.estimated_impact}</p>}
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* Recommendations */}
      <Section icon={ListChecks} title="Recommended actions" testid="ai-recs-section">
        <CardList
          items={s.recommendations} testid="ai-recs" empty="No recommendations right now."
          renderItem={(r, i) => (
            <div key={i} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid={`ai-rec-${i}`}>
              <div className="flex items-center gap-2">
                <span className={`h-1.5 w-1.5 rounded-full ${priorityDot(r.priority)}`} />
                <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-400 font-bold">{r.priority}</span>
              </div>
              <p className="mt-1 text-sm text-zinc-100 font-medium">{r.title}</p>
              <p className="mt-1 text-xs text-zinc-400 leading-relaxed">{r.action}</p>
            </div>
          )}
        />
      </Section>

      {/* Questions to explore */}
      <Section icon={MessageSquare} title="Questions to explore" testid="ai-questions-section">
        {(s.questions_to_explore || []).length === 0 ? (
          <p className="text-sm text-zinc-500">Nothing pending.</p>
        ) : (
          <ul className="space-y-1.5">
            {(s.questions_to_explore || []).map((q, i) => (
              <li key={i} className="text-sm text-zinc-300 flex items-start gap-2" data-testid={`ai-question-${i}`}>
                <MessageSquare className="h-3.5 w-3.5 text-zinc-500 mt-0.5" /> {q}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Suggested next import */}
      {suggestion && (
        <section className="rounded-2xl border border-orange-500/30 bg-orange-500/5 p-5 flex items-start gap-3" data-testid="ai-suggest-report">
          <Upload className="h-4 w-4 text-orange-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-zinc-100 font-medium">
              Upload a <span className="capitalize">{suggestion.report_type}</span> report to unlock more analysis
            </p>
            <p className="mt-1 text-xs text-zinc-400">{suggestion.reason}</p>
          </div>
          <a href="/imports" className="text-xs text-orange-400 hover:text-orange-300 font-medium">Go to imports →</a>
        </section>
      )}

      {/* Timeline */}
      <Section icon={Clock} title="Business event timeline" testid="ai-timeline-section">
        <ol className="relative border-l border-zinc-800 pl-5 space-y-4" data-testid="ai-timeline">
          {timeline.map((e) => (
            <li key={e.event_id} className="relative" data-testid={`ai-timeline-${e.event_id}`}>
              <span className="absolute -left-[27px] top-1.5 h-2 w-2 rounded-full bg-zinc-600" />
              <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                {formatDate(e.timestamp)} · <span className="lowercase">{e.kind.replace(".", " → ")}</span>
              </p>
              <p className="mt-0.5 text-sm text-zinc-100">{e.title}</p>
              {e.description && <p className="text-xs text-zinc-400 leading-relaxed">{e.description}</p>}
            </li>
          ))}
        </ol>
      </Section>

      {/* Source transparency footer */}
      <div className="text-[11px] text-zinc-600 flex items-center gap-2 pt-2 border-t border-zinc-900" data-testid="ai-source-note">
        <Info className="h-3 w-3" />
        All AI answers are grounded in Milkath's Business Intelligence Engine · deterministic metrics · no hallucination.
      </div>
    </div>
  );
}
